import { naturalChestOf, PALETTE, simpatiaChestOf, VISUAL_ROLES } from "@eldrun/content";
import type { ShaderMaterial, Vector4 } from "three";
import { withRole } from "./shaders/surface.ts";

/**
 * O brilho dos baús de saque (Fase 15): a peça inteira em dourado claro (papel `tesouro`), com rim forte
 * na silhueta e um pulso lento que passa do HDR no pico (o bloom do Médio e do Alto os pega de longe; no
 * Baixo, sem compositor, ficam claros). Sem arestas nem nós: só a face e o rim. Uniforms do material de
 * superfície, o mesmo programa (nenhum programa novo).
 */

/** Período do pulso (s). */
export const TREASURE_PULSE_PERIOD = 2.4;

/** Baú de saque (natural ou de Simpatia): os que brilham até serem abertos. O Baú de Madeira do jogador, não. */
export const isTreasureChest = (itemId: string): boolean => naturalChestOf(itemId) !== undefined || simpatiaChestOf(itemId) !== undefined;

/** Variante do material das estruturas no papel `tesouro`, na cor do tesouro. */
export function createTreasureMaterial(base: ShaderMaterial): ShaderMaterial {
  return withRole(base, "tesouro", PALETTE.tesouro.hex);
}

/**
 * Acento e rim do pulso no instante `now` (s). Com "Reduzir lampejos", fixo e no máximo 1 (nada pisca nem
 * estoura no bloom).
 */
export function treasureGlow(now: number, reduceFlashes: boolean): { accent: number; rim: number } {
  const r = VISUAL_ROLES.tesouro;
  if (reduceFlashes) return { accent: Math.min(r.accent, 1), rim: Math.min(r.rim, 1) };
  const k = 0.5 - 0.5 * Math.cos((now / TREASURE_PULSE_PERIOD) * Math.PI * 2);
  // Pulso suave, mais tempo perto do repouso: o pico é um lampejo curto, não um piscar.
  const pulse = k * k;
  return { accent: r.accent + (r.accentPeak - r.accent) * pulse, rim: r.rim * (0.8 + 0.4 * pulse) };
}

/** Escreve o pulso nos uniforms do material do tesouro. */
export function pulseTreasure(material: ShaderMaterial, now: number, reduceFlashes: boolean): void {
  const glow = treasureGlow(now, reduceFlashes);
  (material.uniforms["uStyleAccent"]!.value as Vector4).w = glow.accent;
  (material.uniforms["uStyleRole"]!.value as Vector4).w = glow.rim;
}
